/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useHeroSection } from "@/hooks/useHeroSection";
import Navbar from "@/shared/Navbar";
import Image from "next/image";
import { motion } from "framer-motion";
import { usePages } from "@/hooks/usePages";
import { useMemo } from "react";
import Link from "next/link";

const ProFinderPage = () => {
  const { data: pages, loading } = usePages();
  const HeroSection = useHeroSection({
    title: "Make Smart Decisions with Trusted Local Experts",
    subtitle:
      "Real estate can be overwhelming and expensive without the right support. Our secure Digital Concierge, powered by trusted local professionals, ensures accountability and efficiency throughout your home-buying journey.",
    buttonText: "Build My AskLocal Certified Team",
    backgroundImage: "/pro-finder/pro-finder-hero.png",
    buttonLink: "/storefront",
    gradientFrom: "#000000",
    gradientVia: "#1A1A1A",
    gradientTo: "transparent",
  });

  const steps = [
    {
      step: "Step 1",
      title: "Tell Us Your Market & Goals",
      description:
        "Share your location, goals, and preferences — we’ll personalize your experience immediately.",
      image: "/pro-finder/card_1.png",
    },
    {
      step: "Step 2",
      title: "Match with Certified Experts",
      description:
        "Get introduced to top-rated agents, lenders, and professionals who are trusted in your area.",
      image: "/pro-finder/card_2.png",
    },
    {
      step: "Step 3",
      title: "Manage & Monitor Everything in One Place",
      description:
        "Chat, compare options, and track your progress easily — all inside your AskLocal dashboard.",
      image: "/pro-finder/crad_3.png",
    },
  ];
  const howToHubDropdown = useMemo(() => {
    const staticPages = [
      {
        label: "Property Intelligence Report",
        href: "/how-to-hub/property-intelligence-report",
      },
      { label: "Mortgage Guide", href: "/how-to-hub/mortgage-guide" },
    ];

    const dynamicPages =
      pages?.map((p: any) => ({
        label: p.title || "Untitled",
        href: `/how-to-hub/${p.slug}`,
      })) || [];

    return [
      ...(dynamicPages.length
        ? dynamicPages
        : loading
        ? [{ label: "Loading...", href: "#" }]
        : []),
      ...staticPages,
    ];
  }, [pages, loading]);
  return (
    <div className="bg-white">
      {/* 🔹 Navbar */}
      <Navbar
        defaultLogo="/logo/asklocal-white-logo.png"
        scrolledLogo="/logo/color-full-logo.png"
        isLight
        labelColor="#ffffff"
        labelHoverColor="#EB9C2C"
        buttonBg="#EB9C2C"
        buttonTextColor="#FFFFFF"
        buttonHoverBg="#4177BC"
        buttonHoverText="#ffffff"
        ctaText="Looking to Buy/Sell?"
          ctaLink="/buy-sell"
        navItems={[
          { label: "Home", href: "/" },
          {
            label: "How-To-Hub",
            dropdown: howToHubDropdown, // ✅ dynamic + static pages
          },
          { label: "About Us", href: "/about-us" },
          { label: "Log In", href: "https://portal.asklocal.com/" },
        ]}
      />

      {/* 🔹 Hero Section */}
      {HeroSection}

      {/* 🔹 How Pro-Finder Works */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
        className="py-10 px-4 sm:px-8 md:px-16 lg:px-24 text-center"
      >
        <motion.h2
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.3 }}
          className="text-4xl sm:text-4xl md:text-[78px] judson-regular text-[#000000] mb-6"
        >
          How Pro-Finder Works
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="text-[#938E8E] inter-regular text-lg max-w-[674px] mx-auto mb-16"
        >
          Without a vetted team, you face hidden costs, inefficient workflows,
          and unclear responsibilities. That’s why Pro-Finder exists: to connect
          you with professionals who understand your market and your goals
        </motion.p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-[1200px] mx-auto">
          {steps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              // whileHover={{ scale: 1.04, y: -6 }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 15,
                delay: i * 0.1,
              }}
              viewport={{ once: true }}
              className="relative bg-[#FFFFFF] border border-[#4177BC] rounded-2xl p-2.5 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col items-center"
            >
              <motion.div
                whileHover={{ y: -3 }}
                transition={{ duration: 0.2 }}
                className="absolute bg-[#FFFFFF] text-[#4177BC] px-6 py-1.5 rounded-b-lg text-xl inter-semibold"
              >
                {step.step}
              </motion.div>

              <Image
                src={step.image}
                alt={step.title}
                width={380}
                height={280}
                className="rounded-lg w-[372px] h-[257px] mb-5 object-cover"
              />

              <h4 className="text-[#000000] text-2xl inter-semibold mb-2">
                {step.title}
              </h4>
              <p className="text-[#938E8E] inter-regular text-sm leading-relaxed">
                {step.description}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* 🔹 Trusted Local Experts Section */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="bg-[#FAFAFA]"
      >
        <section className="py-15 px-4 sm:px-8 md:px-16 lg:px-24 max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.4 }}
            className="flex justify-center"
          >
            <Image
              src="/pro-finder/trustedHero.png"
              alt="Local Experts"
              width={550}
              height={400}
              className="rounded-2xl w-[577px] h-[440px] object-cover"
            />
          </motion.div>

          <motion.div
            whileHover={{ x: 4 }}
            transition={{ duration: 0.3 }}
            className="space-y-5"
          >
            <ul className="space-y-8 text-[#111827] inter-regular text-lg">
              <li>
                <span className="inter-semibold">
                  • Trusted & Local Professionals —
                </span>{" "}
                Only certified experts{" "}
                <span className="lg:ml-4">who know your area.</span>
              </li>
              <li>
                <span className="inter-semibold">• Transparent Process —</span>{" "}
                See costs, steps, and timelines{" "}
                <span className="lg:ml-4">up front.</span>
              </li>
              <li>
                <span className="inter-semibold">
                  • Single Platform Efficiency —
                </span>{" "}
                One space for team{" "}
                <span className="lg:ml-4">members, documents & updates.</span>
              </li>
            </ul>
          </motion.div>
        </section>
      </motion.section>

      {/* 🔹 Make Smart Decisions Section */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
        className="py-10 bg-white text-center"
      >
        <motion.h2
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.3 }}
          className="text-3xl sm:text-4xl md:text-[79px] judson-regular text-[#000000] mb-5"
        >
          Make Smart Decisions With <br /> Trusted Local Experts
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-[#938E8E] inter-regular text-lg mb-16 max-w-[940px] px-8 mx-auto leading-relaxed"
        >
          Real estate can be overwhelming and expensive without the right
          support. Our secure Digital Concierge, powered by trusted local
          professionals, ensures accountability and efficiency throughout your
          homebuying journey. With our Profinder tool, you’ll connect with
          agents and vendors who truly understand your market and needs, saving
          you time, money, and stress while guiding you every step of the way
        </motion.p>

        <motion.div
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.3 }}
          className="max-w-[1100px] mx-auto p-3 rounded-2xl overflow-hidden"
        >
          <Image
            src="/pro-finder/BottomImage.png"
            alt="Experts"
            width={1200}
            height={600}
            className="object-cover w-full h-[505px] rounded-2xl"
          />
        </motion.div>
      </motion.section>

      {/* 🔹 Final CTA */}
      <section className="w-full bg-[#F9FAFB] py-16 sm:py-20 md:py-24 px-4 sm:px-6 text-center border-t-10 border-[#4177BC] rounded-t-3xl">
        {/* --- Heading --- */}
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[64px] judson-regular text-[#000000] mb-5 sm:mb-6 leading-snug max-w-3xl mx-auto px-2"
        >
          Ready to Build Your Team
        </motion.h2>

        {/* --- Description --- */}
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-[#6B7280] inter-regular text-sm sm:text-base md:text-lg max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed"
        >
          Connect with the right local professionals and let AskLocal guide you
          through the home-buying process with clarity and confidence Build My
          AskLocal Certified Team
        </motion.p>

        {/* --- Button --- */}

        <Link href="/storefront">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex justify-center"
          >
            <button className="bg-[#EB9C2C] hover:bg-[#4177BC] text-white font-semibold text-sm sm:text-base px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg shadow-md transition-all duration-300">
              Build Your Team
            </button>
          </motion.div>
        </Link>
      </section>
    </div>
  );
};

export default ProFinderPage;
